import React from 'react';
import { Icon } from './Icon.jsx';

/**
 * v2.0 (A4) — Subject switcher for approvers / super-approvers.
 *
 * Three states:
 *   • 'self'           — "My time" (default; what employees always see)
 *   • 'team'           — "Team view" (aggregate table; only meaningful on
 *                         the Summary tab — App.jsx routes accordingly)
 *   • <email>          — drilled into a specific report (read-only Today
 *                         and Summary for that person)
 *
 * Renders nothing for employees so the chrome stays empty on their view.
 */
export function SubjectSwitcher({
  subject,
  role,
  reports,
  onChange,
}) {
  if (role !== 'approver' && role !== 'super_approver') return null;
  if (!reports || reports.length === 0) return null;
  const isReport = subject !== 'self' && subject !== 'team';
  const selectedReport = isReport
    ? reports.find((r) => r.email === subject)
    : null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: 4,
        background: '#fff',
        border: '1px solid var(--sp-border)',
        borderRadius: 12,
        width: 'fit-content',
        flexShrink: 0,
      }}
    >
      <Tab active={subject === 'self'} onClick={() => onChange('self')}>
        <Icon name="clock" size={13} /> My time
      </Tab>
      <Tab active={subject === 'team'} onClick={() => onChange('team')}>
        <Icon name="users" size={13} /> Team view{' '}
        <span
          style={{
            marginLeft: 4,
            padding: '0 6px',
            borderRadius: 999,
            background: 'var(--sp-bg-muted)',
            fontSize: 11,
            color: 'var(--sp-fg-2)',
          }}
        >
          {reports.length}
        </span>
      </Tab>
      {isReport && (
        <Tab active onClick={() => {}}>
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'var(--sp-mint-100)',
              color: '#007C7C',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 700,
            }}
          >
            {(selectedReport?.name || subject)
              .split(' ')
              .map((s) => s[0])
              .slice(0, 2)
              .join('')}
          </span>
          {selectedReport?.name || subject}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange('team');
            }}
            style={{
              all: 'unset',
              cursor: 'pointer',
              marginLeft: 4,
              color: 'var(--sp-fg-3)',
              display: 'inline-flex',
            }}
            title="Back to Team view"
          >
            <Icon name="x" size={11} />
          </button>
        </Tab>
      )}
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 11px',
        borderRadius: 8,
        whiteSpace: 'nowrap',
        fontSize: 13,
        fontWeight: 600,
        color: active ? '#fff' : 'var(--sp-fg-2)',
        background: active ? 'var(--sp-fg-1)' : 'transparent',
        transition: 'background .12s, color .12s',
      }}
    >
      {children}
    </button>
  );
}
